import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Link, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";

import { useDropzone } from "react-dropzone";

export default function UploadFile(props) {
  const {
    fileType,
    files = [],
    onSelectFile,
    onRemoveFile,
    isFullWidth,
    disabled,
    isVisibleFile,
  } = props;

  const onDropFile = useCallback(
    (value) => {
      onSelectFile(value);
    },
    [onSelectFile]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDropFile,
    accept: fileType,
    disabled,
  });

  const onRemove = (index) => () => {
    onRemoveFile(index);
  };

  return (
    <Box>
      <Box
        sx={{
          ...(isFullWidth && {
            width: "100%",
            height: 56,
          }),
          ...(disabled && {
            opacity: 0.5,
          }),
          marginBottom: 25,
          height: 174,
          borderRadius: 4,
          border: "dashed 2px #cbcbcb",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          cursor: "pointer",
          "&:hover": {
            opacity: 0.5,
          },
        }}
        /* eslint-disable react/jsx-props-no-spreading */
        {...getRootProps()}
      >
        <input {...getInputProps()} multiple />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            ...(isFullWidth && { flexDirection: "row-reverse" }),
          }}
        >
          <p>Drop images here</p>
          <CloudUploadIcon
            sx={{
              width: 56,
              height: 56,
              ...(isFullWidth && { width: 26, height: 26, marginRight: 10 }),
            }}
          />
        </Box>
      </Box>
      {isVisibleFile && (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            overflowX: "auto",
            paddingTop: "20px",
          }}
        >
          {files.map((img, index) => (
            <FileUpload key={img.name} image={img} onRemove={onRemove(index)} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export function ImageView({ image, onRemove, disabled }) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        marginRight: 15,
        maxWidth: 150,
      }}
    >
      {!disabled && (
        <Box
          sx={{
            position: "absolute",
            cursor: "pointer",
            right: -10,
            top: -20,
            "&:hover": {
              color: "red",
            },
          }}
          aria-hidden="true"
          onClick={onRemove}
        >
          <HighlightOffIcon />
        </Box>
      )}
      <Link href={image} target="_blank">
        <Box
          sx={{
            width: "125px",
            height: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #e0e0e0",
            color: "#aeaeae",
            backgroundColor: "#f1f1f1",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              margin: "auto",
              objectFit: "contain",
              backgroundSize: "cover",
            }}
            style={{ backgroundImage: `url(${image})` }}
          />
        </Box>

        <Typography
          variant="body2"
          sx={{
            width: 150,
            wordBreak: "break-word",
            fontSize: 12,
            textAlign: "center",
          }}
        >
          {image.name}
        </Typography>
      </Link>
    </Box>
  );
}

function FileUpload(props) {
  const { image, onRemove } = props;

  const [file, setFile] = useState({});
  useEffect(() => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setFile(reader.result);
    };
    if (image) {
      reader.readAsDataURL(image);
    }
  }, [setFile, image]);

  return <ImageView image={file} onRemove={onRemove} />;
}

FileUpload.propTypes = {
  onRemove: PropTypes.func.isRequired,
  image: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
};

ImageView.propTypes = {
  onRemove: PropTypes.func.isRequired,
  image: PropTypes.oneOfType([
    PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    PropTypes.string,
  ]).isRequired,
  disabled: PropTypes.bool,
};
ImageView.defaultProps = {
  disabled: false,
};
UploadFile.propTypes = {
  onSelectFile: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    )
  ),
  fileType: PropTypes.string,
  isFullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  isVisibleFile: PropTypes.bool,
};
UploadFile.defaultProps = {
  files: [],
  isFullWidth: false,
  disabled: false,
  isVisibleFile: false,
  fileType: "",
};
